// Copyright (c) 2023 Jürgen Gebath
//
// This software is released under the MIT License.
// https://opensource.org/licenses/MIT
import React, { useCallback, useEffect, useMemo } from "react";
//import { scheduledEvents } from "../data/scheduledEvents";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Typography,
} from "@mui/material";
import {
  format,
  closestIndexTo,
  isDate,
  isAfter,
  isEqual,
  addMonths,
  getTime,
} from "date-fns";
import { de } from "date-fns/locale";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTheme } from "@mui/material/styles";

export default function NextEvents(props) {
  const theme = useTheme();
  const [expanded, setExpanded] = React.useState(false);
  const [loadMorePrevEventsDisabled, setLoadMorePrevEventsDisabled] =
    React.useState(false);
  const [loadMoreUpcomingEventsDisabled, setLoadMoreUpcomingEventsDisabled] =
    React.useState(false);
  const [scheduledEvents, setScheduledEvents] = React.useState([]);
  const setRefDate = (ref) => {
    if (isDate(ref)) {
      return ref;
    } else {
      return new Date();
    }
  };
  const refDate = useMemo(() => setRefDate(props.date), [props.date]);

  const [eventsToShow, setEventsToShow] = React.useState([]);

  const initEventsToShow = useCallback((refDate, inputEvents) => {
    let closestIndexToToday = closestIndexTo(
      refDate,
      inputEvents.map((event) => {
        return event.date;
      })
    );
    const events = [];
    let i = 0;

    if (typeof closestIndexToToday != "undefined") {
      i = closestIndexToToday;
    }

    // add the upcoming events from the current month
    while (
      i < inputEvents.length &&
      inputEvents[i].date.getMonth() <= refDate.getMonth()
    ) {
      if (
        isAfter(inputEvents[i].date, refDate) ||
        isEqual(inputEvents[i].date, refDate)
      ) {
        events.push(inputEvents[i]);
      }
      i += 1;
    }
    // add the events from the next month
    while (
      i < inputEvents.length &&
      inputEvents[i].date.getMonth() === addMonths(refDate, 1).getMonth()
    ) {
      events.push(inputEvents[i]);
      i += 1;
    }
    setEventsToShow(events);
  }, []);

  const loadData = useCallback(
    (refDate) => {
      fetch(process.env.PUBLIC_URL + "/data/scheduledEvents.json", {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        cache: "no-cache",
      })
        .then(function (originalResponse) {
          return originalResponse.json();
        })
        .then(function (jsonResponse) {
          let jsonRespWithDate = jsonResponse.map((element) => {
            let dateArray = element.date.match(
              /^(\d{4})\.(\d{2})\.(\d{2})\s*(\d{2})*:*(\d{2})*$/
            );
            element.date = new Date(
              dateArray[1],
              dateArray[2] - 1,
              dateArray[3],
              dateArray[4],
              dateArray[5]
            );
            return element;
          });
          setScheduledEvents(jsonRespWithDate);
          initEventsToShow(refDate, jsonRespWithDate);
        })
        .catch((err) => console.log(err));
    },
    [initEventsToShow]
  );

  useEffect(() => {
    loadData(refDate);
  }, [refDate, loadData]);

  function loadMorePrevEvents() {
    const events = [];
    const indexOfFirstShownEvent = scheduledEvents.indexOf(eventsToShow[0]);
    const indexOfLastShownEvent = scheduledEvents.indexOf(
      eventsToShow[eventsToShow.length - 1]
    );
    let i =
      indexOfFirstShownEvent === -1
        ? scheduledEvents.length - 5
        : indexOfFirstShownEvent - 5;
    let j =
      indexOfLastShownEvent === -1
        ? scheduledEvents.length - 1
        : indexOfLastShownEvent;

    if (i < 0) {
      i = 0;
      setLoadMorePrevEventsDisabled(true);
    }

    while (i <= j) {
      events.push(scheduledEvents[i]);
      i += 1;
    }
    setEventsToShow(events);
  }

  function loadMoreUpcomingEvents() {
    const events = [];
    const indexOfFirstShownEvent = scheduledEvents.indexOf(eventsToShow[0]);
    const indexOfLastShownEvent = scheduledEvents.indexOf(
      eventsToShow[eventsToShow.length - 1]
    );
    let i =
      indexOfFirstShownEvent === -1
        ? scheduledEvents.length
        : indexOfFirstShownEvent;
    let j =
      indexOfLastShownEvent === -1
        ? scheduledEvents.length
        : indexOfLastShownEvent + 5;
    if (j >= scheduledEvents.length) {
      j = scheduledEvents.length;
      setLoadMoreUpcomingEventsDisabled(true);
    }
    while (i < j) {
      events.push(scheduledEvents[i]);
      i += 1;
    }
    setEventsToShow(events);
  }

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <div>
      <Button
        id="btnLoadMorePrevEvents"
        size="small"
        onClick={loadMorePrevEvents}
        variant="outlined"
        disabled={loadMorePrevEventsDisabled}
        sx={{ color: theme.palette.primary.light }}>
        Frühere
      </Button>
      {eventsToShow.length > 0 &&
        eventsToShow.map((event, i) => {
          const { id, date, location, subject, exercisePreparation, service } =
            event;
          const panelName = getTime(date);
          const formatedEventDate = format(date, "dd. MMMM yyyy   HH:mm", {
            locale: de,
          });

          return (
            <Accordion
              key={id}
              expanded={expanded === panelName}
              onChange={handleChange(panelName)}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                id={id + "-header"}>
                <Typography>{formatedEventDate + "  -  " + subject}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  <strong>Ort:</strong> {location}
                </Typography>
                {typeof exercisePreparation !== "undefined" && (
                  <Typography>
                    <strong>Übungsvorbereitung:</strong> {exercisePreparation}
                  </Typography>
                )}
                {typeof service !== "undefined" && (
                  <Typography>
                    <strong>Servierdienst:</strong> {service}
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}
      <Button
        id="btnLoadMoreUpcomingEvents"
        size="small"
        onClick={loadMoreUpcomingEvents}
        variant="contained"
        disabled={loadMoreUpcomingEventsDisabled}
        sx={{ backgroundColor: theme.palette.primary.main }}>
        Weitere
      </Button>
    </div>
  );
}
